import * as React from 'react'
import { JsonLd } from 'react-schemaorg'
import { Offer as SchemaOffer, Product as SchemaProduct, UnitPriceSpecification } from 'schema-dts'

import {
  createCanonicalPdpUrl,
  createPdpUrl,
} from '../../../../../app/_components/navigation/createPdpUrl'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { shortenShopifyId } from '../../../../../app/_utils/shortenShopifyId'
import { canonicalBaseUrls } from '../../../../../constants/figs'
import { getDollarsAndCentsForAmount } from '../../../../../lib/utils/currency'
import { formatPrettyTitlePG } from '../../../../../lib/utils/unorganized'
import {
  Product,
  ProductGroup,
  ProductReview,
  ProductReviewSet,
  Variant,
} from '../../../../../types/graphql'

const figsLogo =
  'https://cdn.shopify.com/s/files/1/0139/8942/files/FIGS_LOGO_BLACK-02-large.png?1998072' as const

type GeneratePriceSpecificationProps = {
  priceInDollars: number
  currency: string
  compareAtPriceInDollars?: number | null
}

function generatePriceSpecification({
  priceInDollars,
  currency,
  compareAtPriceInDollars,
}: GeneratePriceSpecificationProps): UnitPriceSpecification[] {
  const regularPriceInDollars = compareAtPriceInDollars ?? priceInDollars
  const discountPriceInDollars = compareAtPriceInDollars ? priceInDollars : null

  const specifications: UnitPriceSpecification[] = [
    {
      '@type': 'UnitPriceSpecification',
      'price': regularPriceInDollars,
      'priceCurrency': currency,
      'priceType': discountPriceInDollars ? 'https://schema.org/ListPrice' : undefined,
    },
  ]

  if (discountPriceInDollars) {
    specifications.push({
      '@type': 'UnitPriceSpecification',
      'price': discountPriceInDollars,
      'priceCurrency': currency,
    })
  }

  return specifications
}

type FindProductImageUrlsProps = {
  product: Product
}

function findProductImageUrls({ product }: FindProductImageUrlsProps): Array<string> {
  const urls =
    product.images?.nodes
      .concat() // create mutable version of read only array
      .sort(({ index: a }, { index: b }) => a - b)
      .map(img => img.source) ?? []
  return urls.length > 0 ? urls : [figsLogo]
}

type generateOfferProps = {
  variant: Variant
  product: Product
  productGroup: ProductGroup
  locale: string
}

function generateOffer({
  variant,
  product,
  productGroup,
  locale,
}: generateOfferProps): SchemaOffer {
  const imageUrl = findProductImageUrls({
    product,
  })[0]!
  const today = new Date(Date.now()).toISOString().substring(0, 10)
  const url =
    'https://www.wearfigs.com' +
    createPdpUrl({
      productGroupHandle: productGroup.handle,
      collectionHandle: null,
      colorKey: variant.rawColor ?? variant.color,
      isKit: product.rawCategory === 'Product Kit',
      isExclusive: product.exclusive,
      genderKey: productGroup.genderCategory,
      fitKey: variant.rawFit,
      sizeKey: variant.rawSize,
    })

  const priceInCents = variant.discountPrice ?? variant.price
  const compareAtPriceInCents = variant.discountPrice ? variant.price : undefined
  const currency = variant.currencyType

  const validSchema: SchemaOffer = {
    '@type': 'Offer',
    'image': imageUrl,
    'priceValidUntil': today,
    'url': url,
    'sku': shortenShopifyId(variant.shopifyId),
    'gtin': variant.upc,
    'priceSpecification': generatePriceSpecification({
      priceInDollars: getDollarsAndCentsForAmount({
        value: priceInCents,
        unit: 'cents',
        currency,
        locale,
      }).dollars,
      currency,
      compareAtPriceInDollars: compareAtPriceInCents
        ? getDollarsAndCentsForAmount({
            value: compareAtPriceInCents,
            unit: 'cents',
            currency,
            locale,
          }).dollars
        : undefined,
    }),
  }

  // TODO: Figure out why we have 'availability' here even though
  // after a schema version upgrade it says that we shouldn't.
  const result = {
    ...validSchema,
    availability: variant.soldOut ? 'http://schema.org/SoldOut' : 'http://schema.org/InStock',
  }
  return result as unknown as SchemaOffer
}

type PdpSchemaTagsProps = {
  product: Product
  productGroup: ProductGroup
  productReviews?: ProductReviewSet | null
  variant: Variant
}

export const PdpSchemaTags: React.FC<PdpSchemaTagsProps> = ({
  variant,
  productGroup,
  product,
  productReviews,
}) => {
  const { locale } = useLocalization()

  const canonicalUrl = createCanonicalPdpUrl({
    productGroupHandle: productGroup.handle,
    isKit: product.rawCategory === 'Product Kit',
    isExclusive: product.exclusive,
    genderKey: productGroup.genderCategory,
  })

  const offers = product.variants?.map(v =>
    generateOffer({
      variant: v,
      product,
      productGroup,
      locale,
    })
  )

  const rawMaterials = product.rawMaterials?.join('; ')

  return (
    <JsonLd<SchemaProduct>
      item={{
        '@context': 'https://schema.org',
        '@id': canonicalUrl,
        '@type': 'Product',
        'aggregateRating':
          productReviews?.averageScore && productReviews?.totalReviews
            ? {
                '@type': 'AggregateRating',
                'reviewCount': productReviews.totalReviews,
                'ratingValue': productReviews.averageScore,
              }
            : undefined,
        'brand': {
          '@type': 'Brand',
          'name': 'FIGS',
          'logo': {
            '@type': 'ImageObject',
            'name': 'FIGS Logo',
            'contentUrl': figsLogo,
          },
          'slogan': 'Why wear scrubs, when you can wear FIGS?',
          'url': canonicalBaseUrls.home,
        },
        'name':
          productGroup.title && productGroup.title.length > 0
            ? productGroup.title
            : formatPrettyTitlePG(productGroup),
        'description': productGroup.description,
        'color': variant.color,
        'image': findProductImageUrls({
          product,
        }),
        'manufacturer': {
          '@type': 'Organization',
          'name': 'FIGS INC.',
          'url': 'https://www.wearfigs.com',
        },
        'material': rawMaterials,
        'offers': offers,
        'sku': variant.sku,
        'url': canonicalUrl,
        'gtin': variant.upc,
        'gtin12': variant.upc,
        'review':
          productReviews?.reviews.map(({ reviewer, title, score, content }: ProductReview) => ({
            '@type': 'Review',
            'author': {
              '@type': 'Person',
              'name': reviewer,
            },
            'name': title,
            'description': productGroup.description,
            'itemReviewed': productGroup.title,
            'reviewBody': content,
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': score,
            },
          })) || [],
      }}
    />
  )
}
